import type { Issue, Me, Overview } from '@ft/shared';
import { quarterOf } from '@ft/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Cust, useLoad } from '../components/ui';
import { Chip, CollapseAll, Panel } from '../lib/collapse';
import { useI18n } from '../i18n';
import { getAreaName } from '../lib/area';
import { dfmt, eur, eur2, pct } from '../lib/format';
import { issueText } from './IssuesPage';
import { AreasOverviewPanel } from '../components/AreasOverviewPanel';
import { HealthPill, Track } from './ProjectsPage';
import { StartBento, viewsFor } from './StartBento';

function HeroLines() {
  const wave = Array.from({ length: 26 }, (_, i) => {
    const y = -40 + i * 9;
    return <path key={i} d={`M-20 ${y} C 300 ${y + 120}, 520 ${y - 60}, 820 ${y + 70} S 1300 ${y - 30}, 1640 ${y + 110}`} />;
  });
  return (
    <svg viewBox="0 0 1600 120" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <g fill="none" stroke="var(--hero-line)" strokeWidth="1.2" opacity=".55">{wave}</g>
    </svg>
  );
}

const ISSUE_TONE = { neg: 'neg', crit: 'crit', info: 'neu' } as const;
const MIO = (cents: number) => eur(cents);

/** Startseite als Cockpit: Kennzahlen des Jahres, offene Punkte, Projekte mit Handlungsbedarf, Abrechnung pro Quartal. */
/** Startseite: Kacheln je Sicht (Delivery Head, Admin, Finance, PM); Lesende ohne Delivery Head behalten die schlanke Ansicht. */
export function StartPage({ me }: { me: Me }) {
  if (me.areas.length > 0 && viewsFor(me).length > 0) return <StartBento me={me} />;
  return <ClassicStart me={me} />;
}

function ClassicStart({ me }: { me: Me }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const year = new Date().getFullYear();
  const hasArea = me.areas.length > 0;
  const { data } = useLoad<Overview>(hasArea ? `/api/overview?year=${year}` : null);
  // Rolle „lesend“ (ohne Delivery Head): nur Kennzahlen – keine offenen Punkte/To-dos, keine Projektliste, keine einzelnen Rechnungen
  const lean = me.role === 'reader' && !me.deliveryHead;
  const canInvoices = me.sections.includes('invoicing');
  const { data: issues } = useLoad<Issue[]>(hasArea && !lean && me.sections.includes('issues') ? '/api/issues' : null);
  const [mine, setMine] = useState(false);
  const first = me.name.split(' ')[0] ?? me.name;

  const isMine = (p: { pm: { id: string } | null; deputy: { id: string } | null }) => p.pm?.id === me.id || p.deputy?.id === me.id;
  const projects = (data?.projects ?? []).filter((p) => !mine || isMine(p));
  const target = projects.reduce((s, p) => s + (p.targetCents ?? 0), 0);
  const ordered = projects.reduce((s, p) => s + p.orderedCents, 0);
  const billed = projects.reduce((s, p) => s + p.billedCents, 0);
  const open = projects.reduce((s, p) => s + p.openCents, 0);
  const pace = data?.pace ?? 0;
  const myIssues = (issues ?? []).filter((i) => !mine || i.pms.some((p) => p.id === me.id)).filter((i) => me.role === 'admin' || !i.link.startsWith('/verwaltung'));
  const risky = projects.filter((p) => p.health === 'behind' || p.health === 'uncovered').sort((a, b) => (b.targetCents ?? 0) - (a.targetCents ?? 0));
  const projNames = new Set(projects.map((p) => p.name));
  // Bei „Meine Projekte“ nur die Rechnungen der eigenen Projekte (PM oder Vertretung)
  const myIds = new Set(projects.map((p) => p.id));
  const custOf = new Map((data?.projects ?? []).map((p) => [p.id, p.customer.name]));
  const quarters = (data?.quarters ?? []).map((q) => {
    if (!mine) return q;
    const own = q.byProject.filter((x) => myIds.has(x.projectId));
    const byC = new Map<string, number>();
    for (const x of own) { const c = custOf.get(x.projectId) ?? '–'; byC.set(c, (byC.get(c) ?? 0) + x.cents); }
    return { ...q, billedCents: own.reduce((s, x) => s + x.cents, 0), byCustomer: [...byC].map(([name, cents]) => ({ name, cents })).sort((a, b) => b.cents - a.cents) };
  });
  const recent = (data?.recentInvoices ?? []).filter((i) => !mine || projNames.has(i.project.name)).slice(0, 6);
  const qMax = Math.max(1, ...quarters.map((q) => q.billedCents));
  const nowQ = quarterOf(new Date().toISOString().slice(0, 10));

  const quarterBox = (
    <Panel id="start.quarters" title={t('ck.quarters', { y: year })} hint={canInvoices ? t('ck.quarterHint') : undefined}
      summary={<>{quarters.filter((q) => q.billedCents).map((q) => <Chip key={q.q} tone={q.q === nowQ ? 'info' : undefined}>Q{q.q} <b>{eur(q.billedCents)}</b></Chip>)}</>}>
      <div className="qchart" role="list">
        {quarters.map((q) => {
          const tip = [`Q${q.q}: ${eur(q.billedCents)}`, ...q.byCustomer.map((c) => `${c.name}: ${eur(c.cents)}`)].join('\n');
          return (
            <button key={q.q} role="listitem" className={`qcol${q.q > nowQ ? ' future' : ''}`} title={tip} aria-label={tip.replace(/\n/g, ', ')} onClick={() => canInvoices && nav(`/invoicing?quartal=${year}-Q${q.q}`)}>
              <span className="qv num">{q.billedCents ? eur(q.billedCents) : '–'}</span>
              <span className="qbar"><i style={{ height: `${(q.billedCents / qMax) * 100}%` }} /></span>
              <span className="ql">Q{q.q}</span>
            </button>
          );
        })}
      </div>
    </Panel>
  );

  return (
    <>
      <div className="hero small"><HeroLines /></div>
      <div className="greet">
        <h1>{t('start.hello', { name: first })}</h1>
        <p>{hasArea ? t('start.sub', { area: getAreaName(), role: me.deliveryHead ? (me.role === 'reader' ? t('role.deliveryHead') : `${t(`role.${me.role}`)} + ${t('role.deliveryHead')}`) : t(`role.${me.role}`) }) : t('app.noArea.admin')}</p>
      </div>

      {me.deliveryHead && <AreasOverviewPanel year={year} />}

      {hasArea && (
        <section className="page">
          <div className="pagehead">
            <h2 className="sechead">{t('ck.kpis', { y: year })}</h2>
            <span className="spacer" />
            <CollapseAll ids={lean ? ['start.quarters'] : ['start.issues', 'start.risk', 'start.quarters', ...(canInvoices ? ['start.recent'] : [])]} />
            {!lean && (
              <div className="seg" role="group" aria-label={t('common.filter')}>
                <button aria-pressed={!mine} onClick={() => setMine(false)}>{t('ck.scopeAll')}</button>
                <button aria-pressed={mine} onClick={() => setMine(true)}>{t('ck.scopeMine')}</button>
              </div>
            )}
          </div>

          <div className="kpis">
            <div className="kpi"><div className="lbl">{t('ck.target', { y: year })}</div><div className="val num">{MIO(target)}</div><div className="sub">{t('ck.targetSub', { n: projects.filter((p) => p.targetCents).length })}</div></div>
            <div className="kpi"><div className="lbl">{t('ck.ordered')}</div><div className="val num">{MIO(ordered)}</div><div className="sub">{target ? t('ck.orderedSub', { p: pct(ordered / target) }) : '–'}</div></div>
            <div className="kpi">
              <div className="lbl">{t('ck.billed')}</div><div className="val num">{MIO(billed)}</div>
              {target > 0 && <div className="meter" style={{ margin: '4px 0 2px' }}><i style={{ width: `${Math.min(100, (billed / target) * 100)}%`, background: 'var(--accent)' }} /><span className="pc" style={{ left: `${pace * 100}%` }} /></div>}
              <div className="sub">{target ? t('ck.billedSub', { p: pct(billed / target), pace: pct(pace) }) : '–'}</div>
            </div>
            <div className="kpi"><div className="lbl">{t('ck.open')}</div><div className="val num">{MIO(open)}</div><div className="sub">{t('ck.openSub')}</div></div>
          </div>

          {lean ? quarterBox : (
          <div className="grid2">
            <div className="col">
              <Panel id="start.issues" title={t('ck.myIssues')} actions={<Link className="btn ghost" to="/offene-punkte">{t('ck.allIssues')}</Link>}
                summary={issues ? <>
                  {(['neg', 'crit', 'info'] as const).map((l) => { const n = myIssues.filter((i) => i.level === l).length; return n ? <Chip key={l} tone={l === 'info' ? undefined : l}><b>{n}</b> {t(`issues.level.${l}`)}</Chip> : null; })}
                  {!myIssues.length && <Chip tone="pos">{t('ck.noIssues')}</Chip>}
                </> : undefined}>
                {!issues ? <div className="empty">{t('app.loading')}</div> : !myIssues.length ? <div className="empty">{t('ck.noIssues')}</div> : (
                  <ul className="issues compact">
                    {myIssues.slice(0, 6).map((i) => {
                      const x = issueText(i, t);
                      return (
                        <li key={i.key} className="click" tabIndex={0} onClick={() => nav(i.link)} onKeyDown={(e) => { if (e.key === 'Enter') nav(i.link); }}>
                          <span><span className={`pill ${ISSUE_TONE[i.level]}`}>{t(`issues.level.${i.level}`)}</span></span>
                          <div><div className="t">{x.title}</div><div className="muted small">{x.detail}</div></div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Panel>
              <Panel id="start.risk" title={t('ck.projectsAtRisk')} actions={<Link className="btn ghost" to="/projekte">{t('ck.allProjects')}</Link>}
                summary={data ? <>
                  {risky.filter((p) => p.health === 'behind').length > 0 && <Chip tone="crit"><b>{risky.filter((p) => p.health === 'behind').length}</b> {t('pz.health.behind')}</Chip>}
                  {risky.filter((p) => p.health === 'uncovered').length > 0 && <Chip tone="neg"><b>{risky.filter((p) => p.health === 'uncovered').length}</b> {t('pz.health.uncovered')}</Chip>}
                  {!risky.length && <Chip tone="pos">{t('ck.noRisk')}</Chip>}
                </> : undefined}>
                {!data ? <div className="empty">{t('app.loading')}</div> : !risky.length ? <div className="empty">{t('ck.noRisk')}</div> : (
                  <div className="tablewrap"><table><tbody>
                    {risky.slice(0, 6).map((p) => (
                      <tr key={p.id} className="click" tabIndex={0} onClick={() => nav(`/bestellungen?projekt=${encodeURIComponent(p.name)}`)} onKeyDown={(e) => { if (e.key === 'Enter') nav(`/bestellungen?projekt=${encodeURIComponent(p.name)}`); }}>
                        <td><b style={{ fontWeight: 600 }}>{p.name}</b><div className="muted small">{p.customer.name}{p.pm ? ` · ${p.pm.name}` : ''}</div></td>
                        <td style={{ minWidth: 140 }}><Track p={p} pace={pace} t={t} /></td>
                        <td><HealthPill h={p.health} t={t} /></td>
                      </tr>
                    ))}
                  </tbody></table></div>
                )}
              </Panel>
            </div>

            <div className="col">
              {quarterBox}
              {canInvoices && <Panel id="start.recent" title={t('ck.recent')} actions={<Link className="btn ghost" to="/invoicing">{t('ck.allInvoices')}</Link>}
                summary={recent[0] ? <Chip><b>{recent.length}</b> · {dfmt(recent[0].invoiceDate)}</Chip> : undefined}>
                <div className="tablewrap"><table><tbody>
                  {recent.map((i) => (
                    <tr key={i.id}>
                      <td className="num muted">{dfmt(i.invoiceDate)}</td>
                      <td><Cust name={i.customer.name} /></td>
                      <td>{i.project.name}</td>
                      <td className="r num">{eur2(i.amountCents)}</td>
                    </tr>
                  ))}
                </tbody></table></div>
              </Panel>}
            </div>
          </div>
          )}
        </section>
      )}
    </>
  );
}
